import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { resendVerification } from "../../lib/authApi";
import { ApiError } from "../../lib/api";
import Wordmark from "../../components/Brand/Wordmark";

// Only ever redirect back to a same-origin path. Resolving against
// window.location.origin (rather than a leading-slash regex) is what
// catches browser-normalized tricks like "/\evil.com" or "\\evil.com" —
// URL parsers treat backslashes as slashes, so a naive "starts with /"
// check would wrongly accept them as relative paths. An invalid/absolute
// URL, or one whose origin differs, falls back to the role default.
const safeReturnTo = (value: string | null): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin === window.location.origin) return url.pathname + url.search + url.hash;
  } catch {
    // Malformed input — ignore and fall back to the role default.
  }
  return null;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setUnverified(false);
    // Resolved only on a successful login; navigate() runs after the
    // try/catch/finally so a navigation error can never be mistaken for a
    // failed sign-in.
    let target: string | null = null;
    try {
      const u = await login(email, password);
      const roleDefault = u.userType === "hr" ? "/hr/jobs" : u.userType === "admin" ? "/admin" : "/job-seeker/jobs";
      target = safeReturnTo(searchParams.get("returnTo")) ?? roleDefault;
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_not_verified") {
        setUnverified(true);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
    if (target) navigate(target);
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("Enter your email above first.");
      return;
    }
    setResending(true);
    try {
      const res = await resendVerification(email);
      toast.success(res.message);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-cream">
      {/*
        Left half — editorial framing. Velaigal as the brand moment.
        On mobile, this collapses above the form (smaller).
      */}
      <aside className="relative bg-cream px-8 py-12 lg:px-16 lg:py-20 flex flex-col justify-between">
        {/* Quiet Tamil glyph — a small editorial whisper, not a hero */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-8 right-10 select-none pointer-events-none"
          style={{
            fontFamily: '"Noto Serif Tamil", Georgia, serif',
            fontSize: "72px",
            fontWeight: 500,
            color: "var(--color-ink)",
            opacity: 0.18,
            lineHeight: 1,
          }}
        >
          வே
        </span>

        <Link
          to="/"
          aria-label="Velaigal home"
          className="text-ink no-underline self-start relative z-10"
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="lg:max-w-md mt-12 lg:mt-0 relative z-10">
          <h1
            className="font-display text-3xl lg:text-5xl font-medium text-ink leading-tight tracking-tight velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Welcome back to{" "}
            <span className="text-saffron">Velaigal</span>.
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-6 text-base lg:text-lg text-ink-secondary leading-relaxed">
            Pick up where you left off. Your candidates, your jobs, your
            decisions, all in one place.
          </p>
        </div>

        <span className="hidden lg:block text-[11px] uppercase tracking-widest text-ink-tertiary mt-12">
          Made for hiring teams and jobseekers across the World.
        </span>
      </aside>

      {/*
        Right half — form. Centered with a max-width, paper background to
        create a subtle two-tone split with the cream left side.
      */}
      <section className="bg-paper flex items-center justify-center px-6 py-12 lg:px-16 lg:py-20 border-t lg:border-t-0 lg:border-l border-hairline">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary">
              Sign in
            </span>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            {unverified && (
              <div
                role="alert"
                className="px-4 py-3 border border-warning bg-paper text-sm text-ink rounded space-y-3"
              >
                <p>
                  Verify your email first — check your inbox for the
                  confirmation link we sent you.
                </p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="btn-secondary"
                >
                  {resending ? "Sending…" : "Resend verification email"}
                </button>
              </div>
            )}

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-ink"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-ink"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-ink-tertiary hover:text-saffron transition-colors no-underline"
                >
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-tertiary hover:text-ink transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full"
            >
              {busy ? (
                <>
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                    style={{
                      borderColor: "rgba(244, 240, 232, 0.35)",
                      borderTopColor: "var(--color-cream)",
                    }}
                    aria-hidden="true"
                  />
                  <span>Signing in</span>
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-ink-tertiary">
            New to Velaigal?{" "}
            <Link
              to="/signup/jobseeker"
              className="velaigal-link font-medium"
            >
              Job seeker sign up →
            </Link>{" "}
            <span className="text-ink-tertiary">·</span>{" "}
            <Link
              to="/signup/hr"
              className="velaigal-link font-medium"
            >
              HR sign up →
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
